import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import { Button } from './ui/Button';
import { IconHinweis, IconPfeilLinks, IconPfeilRechts } from './icons';
import { federFlaeche, federIndikator, schrittVarianten, transition } from '../lib/motion';
import { cn } from '../lib/cn';

export interface WizardSchritt {
  id: string;
  titel: string;
  /** Kurzform für die Fortschrittsanzeige. */
  kurz?: string;
  /** Erläuterung unter der Überschrift. */
  hinweis?: string;
  inhalt: ReactNode;
  /** Prüfung beim Weiterblättern. Leeres Array = keine Beanstandung. */
  pruefen?: () => string[];
}

export interface WizardProps {
  schritte: WizardSchritt[];
  /** Beschriftung der Schaltfläche im letzten Schritt. */
  abschlussLabel?: string;
  onAbschluss: () => void;
  onAbbruch?: () => void;
  abbruchLabel?: string;
  /** Meldet jeden Schrittwechsel nach außen. */
  onSchritt?: (index: number, id: string) => void;
  className?: string;
}

/**
 * Geführter Flow mit Fortschrittsanzeige, richtungsabhängigen Schrittübergängen
 * und Prüfung je Schritt. Die Höhe wird weich animiert, damit kein Sprung entsteht.
 */
export function Wizard({
  schritte,
  abschlussLabel = 'Abschließen',
  onAbschluss,
  onAbbruch,
  abbruchLabel = 'Abbrechen',
  onSchritt,
  className,
}: WizardProps) {
  const [index, setIndex] = useState(0);
  const [richtung, setRichtung] = useState<1 | -1>(1);
  const [fehler, setFehler] = useState<string[]>([]);
  const reduziert = useReducedMotion();
  const fehlerSteuerung = useAnimationControls();
  const ueberschrift = useRef<HTMLHeadingElement | null>(null);

  const schritt = schritte[index];
  const letzter = index === schritte.length - 1;
  const fortschritt = ((index + 1) / schritte.length) * 100;

  useEffect(() => {
    onSchritt?.(index, schritte[index]?.id ?? '');
    // Fokus auf die Schrittüberschrift, damit Screenreader den Wechsel mitbekommen.
    ueberschrift.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  function wechsle(zielIndex: number, neueRichtung: 1 | -1) {
    setRichtung(neueRichtung);
    setFehler([]);
    setIndex(zielIndex);
  }

  function zurueck() {
    if (index === 0) return;
    wechsle(index - 1, -1);
  }

  function weiter() {
    const beanstandungen = schritt?.pruefen?.() ?? [];
    if (beanstandungen.length > 0) {
      setFehler(beanstandungen);
      if (!reduziert) {
        void fehlerSteuerung.start({
          x: [0, -6, 6, -4, 4, 0],
          transition: { duration: 0.32, ease: 'easeInOut' },
        });
      }
      return;
    }
    if (letzter) {
      onAbschluss();
      return;
    }
    wechsle(index + 1, 1);
  }

  if (!schritt) return null;

  return (
    <div className={cn('rounded-xl border border-border bg-surface shadow-sm', className)}>
      {/* Fortschritt */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-xs font-medium uppercase tracking-wide text-fg-subtle">
            Schritt {index + 1} von {schritte.length}
          </p>
          <p className="truncate text-xs text-fg-muted">{schritt.kurz ?? schritt.titel}</p>
        </div>

        <div
          className="mt-2 h-1.5 w-full overflow-hidden rounded-pill bg-surface-3"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={schritte.length}
          aria-valuenow={index + 1}
          aria-label="Fortschritt"
        >
          <motion.div
            className="h-full rounded-pill bg-accent"
            initial={false}
            animate={{ width: `${fortschritt}%` }}
            transition={transition(!!reduziert, federIndikator)}
          />
        </div>

        {/* Schrittmarken: bereits abgeschlossene Schritte sind wieder erreichbar. */}
        <ol className="mt-3 hidden flex-wrap gap-1 md:flex">
          {schritte.map((s, i) => {
            const erledigt = i < index;
            const aktiv = i === index;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => erledigt && wechsle(i, -1)}
                  disabled={!erledigt}
                  aria-current={aktiv ? 'step' : undefined}
                  className={cn(
                    'rounded-pill px-2.5 py-1 text-xs font-medium transition-colors duration-fast ease-out',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                    aktiv && 'bg-accent-soft text-accent-soft-fg',
                    erledigt && 'text-fg-muted hover:bg-surface-2 hover:text-fg',
                    !aktiv && !erledigt && 'text-fg-subtle',
                  )}
                >
                  {s.kurz ?? s.titel}
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Inhalt */}
      <motion.div layout transition={transition(!!reduziert, federFlaeche)} className="px-4 py-4">
        <AnimatePresence mode="wait" custom={richtung} initial={false}>
          <motion.section
            key={schritt.id}
            custom={richtung}
            variants={reduziert ? undefined : schrittVarianten}
            initial="eingang"
            animate="aktiv"
            exit="ausgang"
            transition={{ duration: reduziert ? 0 : 0.24, ease: [0.2, 0.8, 0.2, 1] }}
            aria-labelledby={`wizard-titel-${schritt.id}`}
          >
            <h2
              id={`wizard-titel-${schritt.id}`}
              ref={ueberschrift}
              tabIndex={-1}
              className="text-lg font-semibold text-fg focus-visible:outline-none"
            >
              {schritt.titel}
            </h2>
            {schritt.hinweis && (
              <p className="mt-1 text-sm leading-relaxed text-fg-muted">{schritt.hinweis}</p>
            )}

            <div className="mt-4">{schritt.inhalt}</div>
          </motion.section>
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {fehler.length > 0 && (
            <motion.div
              key="wizard-fehler"
              animate={fehlerSteuerung}
              initial={{ opacity: 0, y: -4 }}
              exit={{ opacity: 0 }}
              className="mt-4"
            >
              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                role="alert"
                className="flex gap-3 rounded-lg border border-danger bg-danger-soft px-3 py-2.5"
              >
                <span className="mt-0.5 shrink-0 text-danger-soft-fg">
                  <IconHinweis groesse={18} />
                </span>
                <div className="min-w-0 text-sm text-danger-soft-fg">
                  <p className="font-medium">Angaben unvollständig</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4">
                    {fehler.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Steuerung */}
      <div className="flex items-center justify-between gap-3 border-t border-border bg-surface-2 px-4 py-3">
        <div>
          {index > 0 ? (
            <Button variante="sekundaer" onClick={zurueck} icon={<IconPfeilLinks groesse={18} />}>
              Zurück
            </Button>
          ) : (
            onAbbruch && (
              <Button variante="dezent" onClick={onAbbruch}>
                {abbruchLabel}
              </Button>
            )
          )}
        </div>

        <Button
          onClick={weiter}
          iconRechts={letzter ? undefined : <IconPfeilRechts groesse={18} />}
        >
          {letzter ? abschlussLabel : 'Weiter'}
        </Button>
      </div>
    </div>
  );
}
